import { useEffect, useRef, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import PartnerBannerCarousel from './PartnerBannerCarousel';
import { fetchPublicAds, getCachedPublicAds } from '../utils/publicAds.js';
import './AdBannerSection.css';

import { API_URL as API } from '../config/env.js';

export default function AdBannerSection({ placement = 'desktop' }) {
  const { t } = useLang();
  const cached = getCachedPublicAds();
  const [banners, setBanners] = useState(() => cached?.banner || []);
  const retryRef = useRef(0);
  const timerRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    async function load(force = false) {
      try {
        const data = await fetchPublicAds(API, { force });
        if (cancelled) return;
        setBanners(data.banner || []);
        retryRef.current = 0;
      } catch {
        if (cancelled) return;
        const attempt = retryRef.current + 1;
        retryRef.current = attempt;
        if (attempt <= 4) {
          const delay = Math.min(1500 * 2 ** (attempt - 1), 12000);
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => load(true), delay);
        }
      }
    }

    load(false);

    const onVisible = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  if (!banners.length) return null;

  return (
    <section
      className={`ad-banner-section ad-banner-section--${placement}`}
      aria-label={t.adsTitle || 'Advertisements'}
    >
      <div className="ad-banner-section__wrap">
        <header className="ad-banner-section__head">
          <div className="ad-banner-section__head-main">
            <span className="ad-banner-section__line" aria-hidden />
            <div className="ad-banner-section__head-text">
              <span className="ad-banner-section__eyebrow">{t.adPartnerStrip || 'Our partners'}</span>
              <h2 className="ad-banner-section__title">{t.adsTitle}</h2>
            </div>
          </div>
          <span className="ad-banner-section__badge">{t.adSponsored || 'Sponsored'}</span>
        </header>
        <PartnerBannerCarousel
          banners={banners}
          ariaLabel={t.adPartnerStrip || 'Our partners'}
        />
      </div>
    </section>
  );
}
